import {
  useIsMobile,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSForm,
  WSInputNumber,
  WSSelect,
  WSText
} from "@wingspanhq/fe-component-library";
import { useFormContext } from "react-hook-form";
import * as Yup from "yup";
import { RadioGroupWithCustomInputs } from "../../../components/RadioGroupWithCustomInputs/RadioGroupWithCustomInputs";
import { WeekDay } from "../../types/time";
import styles from "./FieldsPayrollCadence.module.scss";
import { MAX_WEEK_INTERVAL, PayrollCadenceOption } from "./types";

/**
 * The contractor payroll cadence fields — the radio group the payer picks a
 * frequency with, plus the two inline inputs the weekly option carries.
 *
 * Shared by the payroll-schedule settings screen and the payer-onboarding
 * payroll-settings step, which write the same `PayrollSchedule` through
 * {@link selectorPayrollScheduleUpdate} and read it back through
 * {@link selectorPayrollCadenceFormData}.
 *
 * Expects to be rendered inside a `WSForm` whose data extends
 * {@link PayrollCadenceFormData}, and whose validation schema spreads
 * {@link payrollCadenceValidationSchema}.
 */
const WEEK_DAYS = Object.values(WeekDay);

export const payrollCadenceValidationSchema = {
  interval: Yup.string().oneOf(Object.values(PayrollCadenceOption)).required(),
  every: Yup.number().when("interval", {
    is: PayrollCadenceOption.Week,
    then: Yup.number()
      .required("Required")
      .min(1, "Must be at least 1 week")
      .max(
        MAX_WEEK_INTERVAL,
        "Payroll can run every 1 or 2 weeks. Longer intervals aren't supported yet."
      )
  }),
  dayInInterval: Yup.string()
    .oneOf(Object.values(WeekDay))
    .when("interval", {
      is: PayrollCadenceOption.Week,
      then: Yup.string().oneOf(Object.values(WeekDay)).required("Required")
    })
};

type Props = { label?: string } & WSElementProps;

export const FieldsPayrollCadence: React.FC<Props> = ({
  label,
  ...elementProps
}) => {
  const { setValue } = useFormContext();
  const isMobile = useIsMobile();

  // Touching either inline input is how a payer picks the weekly option, so
  // focus selects the radio it belongs to.
  const selectWeekOption = () =>
    setValue("interval", PayrollCadenceOption.Week);

  return (
    <RadioGroupWithCustomInputs
      {...elementProps}
      label={label}
      name="interval"
      options={[
        { value: PayrollCadenceOption.Monthly, label: "Monthly" },
        { value: PayrollCadenceOption.SemiMonthly, label: "Semi-monthly" },
        { value: PayrollCadenceOption.Everyday, label: "Everyday" },
        {
          value: PayrollCadenceOption.Week,
          label: (
            <WSFlexBox
              wrap={isMobile ? "wrap" : "nowrap"}
              alignItems="center"
              gap="M"
            >
              <WSText.ParagraphSm inline>Every</WSText.ParagraphSm>
              <WSElement className={styles.everyField}>
                <WSForm.Field
                  name="every"
                  component={WSInputNumber}
                  componentProps={{
                    min: 1,
                    max: MAX_WEEK_INTERVAL,
                    onFocus: selectWeekOption
                  }}
                />
              </WSElement>
              <WSText.ParagraphSm inline>week(s) on</WSText.ParagraphSm>
              <WSElement className={styles.weekDayField}>
                <WSForm.Field
                  name="dayInInterval"
                  component={WSSelect}
                  componentProps={{
                    options: WEEK_DAYS.map(day => ({
                      value: day,
                      label: day
                    })),
                    onFocus: selectWeekOption
                  }}
                />
              </WSElement>
            </WSFlexBox>
          )
        }
      ]}
    />
  );
};
